import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function MassDefectAnimation() {
  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    const cycleTime = 4.0;
    const t = totalTime % cycleTime;

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Draw Watermark at bottom-right corner
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('E = mc²', width - 12, height - 12);

    // Top-Left Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Mass Defect & Energy Conversion (E=mc²)', 12, 24);

    // Corner positions for the 4 protons
    const startPos = [
      { x: cx - 100, y: cy - 70 }, // Top-Left
      { x: cx + 100, y: cy - 70 }, // Top-Right
      { x: cx - 100, y: cy + 70 }, // Bottom-Left
      { x: cx + 100, y: cy + 70 }  // Bottom-Right
    ];

    const drawProton = (x: number, y: number, radius = 16) => {
      ctx.fillStyle = '#F87171'; // Red
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      // Label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('p⁺', x, y);
    };

    if (t < 1.0) {
      // Phase 0: 4 separate protons at corners
      startPos.forEach(pos => drawProton(pos.x, pos.y));

      // Phase label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('4 Hydrogen Protons (individual mass)', cx, cy - 110);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '12px sans-serif';
      ctx.fillText('Total mass = 4.032 amu', cx, cy - 90);

    } else if (t < 2.0) {
      // Phase 1: Drift toward center
      const k = t - 1.0; // 0 to 1
      startPos.forEach(pos => {
        const px = pos.x + (cx - pos.x) * k;
        const py = pos.y + (cy - pos.y) * k;
        drawProton(px, py);
      });

      // Phase label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('Attracted by Strong Nuclear Force', cx, cy - 110);

    } else if (t < 2.4) {
      // Phase 2: Merge Flash & Helium appearance
      const k = (t - 2.0) / 0.4; // 0 to 1

      // Draw Helium core appearing (increasing in opacity)
      ctx.fillStyle = `rgba(52, 211, 153, ${k})`;
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(255, 255, 255, ${k})`;
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('He', cx, cy);

      // Draw white explosion flash expanding
      ctx.fillStyle = `rgba(255, 255, 255, ${1 - k})`;
      ctx.beginPath();
      ctx.arc(cx, cy, k * 70, 0, Math.PI * 2);
      ctx.fill();

      // Phase label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('Fusion Occurs!', cx, cy - 110);

    } else if (t < 3.2) {
      // Phase 3: Helium nucleus + lost mass ghost circle
      ctx.fillStyle = '#34D399';
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('He', cx, cy);
      ctx.textBaseline = 'alphabetic';

      // Draw ghost circle "lost mass"
      const ghostX = cx + 50;
      const ghostY = cy - 35;
      ctx.strokeStyle = 'rgba(248, 113, 113, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.arc(ghostX, ghostY, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = 'rgba(248, 113, 113, 0.8)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('lost mass', cx + 62, cy - 31);
      
      // Draw strikethrough line
      ctx.beginPath();
      ctx.moveTo(cx + 60, cy - 34);
      ctx.lineTo(cx + 112, cy - 34);
      ctx.strokeStyle = 'rgba(248, 113, 113, 0.8)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Phase labels
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('1 Helium Nucleus formed', cx, cy - 110);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '12px sans-serif';
      ctx.fillText('Helium mass = 4.002 amu (0.030 amu missing!)', cx, cy - 90);

    } else {
      // Phase 4: Photon emitted, E=mc2 equation fades in below
      // Draw Helium nucleus
      ctx.fillStyle = '#34D399';
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('He', cx, cy);
      ctx.textBaseline = 'alphabetic';

      // Draw ghost circle "lost mass"
      const ghostX = cx + 50;
      const ghostY = cy - 35;
      ctx.strokeStyle = 'rgba(248, 113, 113, 0.3)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.arc(ghostX, ghostY, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = 'rgba(248, 113, 113, 0.5)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('lost mass', cx + 62, cy - 31);
      
      ctx.beginPath();
      ctx.moveTo(cx + 60, cy - 34);
      ctx.lineTo(cx + 112, cy - 34);
      ctx.strokeStyle = 'rgba(248, 113, 113, 0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw yellow photon shooting rightward
      const k = (t - 3.2) / 0.8; // 0 to 1
      const startPhotonX = cx + 22;
      const photonX = startPhotonX + k * (width - startPhotonX - 20);
      
      ctx.fillStyle = '#FCD34D';
      ctx.beginPath();
      ctx.arc(photonX, cy, 6, 0, Math.PI * 2);
      ctx.fill();

      // Glow on photon
      const pGlow = ctx.createRadialGradient(photonX, cy, 0, photonX, cy, 14);
      pGlow.addColorStop(0, 'rgba(252, 211, 77, 0.4)');
      pGlow.addColorStop(1, 'rgba(252, 211, 77, 0)');
      ctx.fillStyle = pGlow;
      ctx.beginPath();
      ctx.arc(photonX, cy, 14, 0, Math.PI * 2);
      ctx.fill();

      // Equation E = mc² fades in below
      const fadeK = Math.min((t - 3.2) / 0.3, 1);
      ctx.fillStyle = `rgba(255, 255, 255, ${fadeK})`;
      ctx.font = 'bold 22px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('E = mc²', cx, cy + 60);

      ctx.fillStyle = `rgba(34, 211, 238, ${fadeK * 0.8})`; // Cyan
      ctx.font = '11px sans-serif';
      ctx.fillText('Missing mass is converted directly to Energy!', cx, cy + 80);

      // Phase label at the top
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Mass defect released as Radiation (Photon)', cx, cy - 110);
    }
  }, []);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
